import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';

type FreeformMultiSelectProps = {
  label: string;
  values: ReadonlyArray<string>;
  onValuesChange: (values: ReadonlyArray<string>) => void;
  placeholder?: string;
  emptyLabel?: string;
  inputPlaceholder?: string;
  containerClassName?: string;
  buttonClassName?: string;
  menuClassName?: string;
  chipClassName?: string;
};

export function FreeformMultiSelect({
  label,
  values,
  onValuesChange,
  emptyLabel = 'None selected',
  inputPlaceholder = 'Type a value and press Enter',
  containerClassName = 'shrink-0 flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700',
  buttonClassName = 'flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-2 py-1.5 text-left text-sm font-normal normal-case tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring',
  menuClassName = 'absolute right-0 z-20 mt-1 w-max min-w-full max-w-xs rounded-md border border-slate-200 bg-white p-2 shadow-lg',
  chipClassName = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-100 px-2 py-0.5 text-xs normal-case text-sky-800',
}: FreeformMultiSelectProps): ReactElement {
  const generatedId = useId();
  const comboboxId = `freeform-multiselect-${generatedId}`;
  const menuId = `${comboboxId}-options`;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    function handleOutsidePointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
    };
  }, []);

  function addValue(rawValue: string): void {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue || values.includes(trimmedValue)) {
      return;
    }

    onValuesChange([...values, trimmedValue]);
  }

  function removeValue(value: string): void {
    onValuesChange(values.filter((existingValue) => existingValue !== value));
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addValue(inputValue);
      setInputValue('');
      return;
    }

    if (event.key === 'Backspace' && inputValue === '' && values.length > 0) {
      removeValue(values[values.length - 1]);
    }
  }

  function getDisplayLabel(): string {
    return values.length > 0 ? values.join(', ') : emptyLabel;
  }

  return (
    <div ref={rootRef} className={containerClassName}>
      {label}
      <div className="relative mt-1">
        <button
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={menuId}
          onClick={() => {
            setIsOpen((previous) => !previous);
            window.setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className={buttonClassName}
        >
          <span className="truncate">{getDisplayLabel()}</span>
          <span className="ml-2 text-slate-500">▾</span>
        </button>

        {isOpen ? (
          <div id={menuId} className={menuClassName}>
            {values.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-1">
                {values.map((value) => (
                  <span key={value} className={chipClassName}>
                    {value}
                    <button
                      type="button"
                      className="text-sky-600 hover:text-sky-900"
                      aria-label={`Remove ${value}`}
                      onClick={() => removeValue(value)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <input
              ref={inputRef}
              type="text"
              className="block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm font-normal normal-case tracking-normal text-slate-900 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={inputPlaceholder}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
